import {
    HttpBackend,
    HttpClient,
    HttpErrorResponse,
    HttpHeaders
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AirspaceAuthorisationDto,
    AirspaceAuthorisationResponse,
    RequestAirspaceAuthorisationCommand,
    SessionExpired,
    OperationAuthorisation,
    parseErrorBody
} from '@flyfreely-portal-ui/flyfreely';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AirspaceCheckService } from '../airspace-check';
import { AirspaceAuthorisationProvider } from './airspace-authorisation-provider';
import { AlternativeAirspaceAuthorisationRequestCommand, AuthorisationError } from './interfaces';
import { mapAuthorisationResult } from './helpers';

/**
 * Routes all authorisation operations to a jurisdiction-specific alternative
 * URL (e.g. USA LAANC) using a short-lived bearer token.
 *
 * HTTP calls are made via a `HttpBackend`-backed client that intentionally
 * bypasses Angular's interceptor chain — the interceptors authenticate against
 * the FlyFreely API, not the external endpoint.
 *
 * Call `configure(url, token)` whenever the URL or token changes (Pipeline 1
 * in `AirspaceAuthorisationManager`). If a call is attempted before
 * `configure` has been called with non-null values, the provider restarts
 * bearer token polling and throws a `SessionExpired` error.
 *
 * In addition to the `AirspaceAuthorisationProvider` interface methods, this
 * provider exposes `findAllForMission` — an external-path-only operation that
 * fetches all authorisations for the current operator session (no mission ID
 * needed) and is used by `AirspaceAuthorisationManager`'s Pipeline 5.
 */
@Injectable()
export class ExternalAuthorisationProvider
    implements AirspaceAuthorisationProvider
{
    private _url: string | null = null;
    private _token: { accessToken: string } | null = null;

    /** True once both a URL and a token have been configured. */
    get isConfigured(): boolean {
        return this._url != null;
    }

    // Bypasses Angular interceptors so the FlyFreely Bearer token is not sent
    // to the external endpoint.
    private readonly http: HttpClient;

    constructor(
        private airspaceCheckService: AirspaceCheckService,
        httpHandler: HttpBackend
    ) {
        this.http = new HttpClient(httpHandler);
    }

    /**
     * Update the alternative URL and bearer token. Called by
     * `AirspaceAuthorisationManager` from Pipeline 1 whenever the
     * `checkInputState$` changes.
     */
    configure(
        url: string | null,
        token: { accessToken: string } | null
    ): void {
        this._url = url;
        this._token = token;
    }

    // ── AirspaceAuthorisationProvider ────────────────────────────────────────

    getPreview(
        command: AlternativeAirspaceAuthorisationRequestCommand
    ): Observable<AirspaceAuthorisationResponse> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .post<AirspaceAuthorisationResponse>(
                `${this._url}/authorisations/preview`,
                command,
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 500) {
                        // Surface provider-level errors as a typed error so
                        // the manager can update authorisationError state.
                        return throwError(
                            () =>
                                ({
                                    code: 500,
                                    message: err.message
                                } as AuthorisationError)
                        );
                    }
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                })
            );
    }

    apply(
        command:
            | RequestAirspaceAuthorisationCommand
            | AlternativeAirspaceAuthorisationRequestCommand
    ): Observable<AirspaceAuthorisationDto[]> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .post<OperationAuthorisation[]>(
                `${this._url}/authorisations`,
                command,
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                }),
                // FIXME: temporary normalisation — remove once the external API
                // returns AirspaceAuthorisationDto[] directly.
                map(result => mapAuthorisationResult(result))
            );
    }

    findById(
        id: string,
        type: AirspaceAuthorisationDto.AuthorisationType = AirspaceAuthorisationDto
            .AuthorisationType.USA_LAANC
    ): Observable<AirspaceAuthorisationDto | null> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .get<OperationAuthorisation>(
                `${this._url}/authorisations/${type}/${id}`,
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                }),
                // FIXME: temporary normalisation
                map(result =>
                    mapAuthorisationResult([result]) != null
                        ? mapAuthorisationResult([result])[0]
                        : null
                )
            );
    }

    cancel(
        id: string,
        type: AirspaceAuthorisationDto.AuthorisationType = AirspaceAuthorisationDto
            .AuthorisationType.USA_LAANC
    ): Observable<AirspaceAuthorisationDto> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .post<OperationAuthorisation>(
                `${this._url}/authorisations/${type}/${id}/cancel`,
                null,
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                }),
                // FIXME: temporary normalisation
                map(result =>
                    mapAuthorisationResult([result]) != null
                        ? mapAuthorisationResult([result])[0]
                        : null
                )
            );
    }

    delete(
        id: string,
        type: AirspaceAuthorisationDto.AuthorisationType = AirspaceAuthorisationDto
            .AuthorisationType.USA_LAANC
    ): Observable<void> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .post<void>(
                `${this._url}/authorisations/${type}/${id}/delete`,
                null,
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                })
            );
    }

    close(
        id: string,
        type: AirspaceAuthorisationDto.AuthorisationType = AirspaceAuthorisationDto
            .AuthorisationType.USA_LAANC
    ): Observable<AirspaceAuthorisationDto> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .post<OperationAuthorisation>(
                `${this._url}/authorisations/${type}/${id}/close`,
                {},
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                }),
                // FIXME: temporary normalisation
                map(result =>
                    mapAuthorisationResult([result]) != null
                        ? mapAuthorisationResult([result])[0]
                        : null
                )
            );
    }

    acknowledgeRescinded(
        id: string,
        type: AirspaceAuthorisationDto.AuthorisationType = AirspaceAuthorisationDto
            .AuthorisationType.USA_LAANC
    ): Observable<AirspaceAuthorisationDto> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .post<OperationAuthorisation>(
                `${this._url}/authorisations/${type}/${id}/acknowledgeRescinded`,
                null,
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                }),
                // FIXME: temporary normalisation
                map(result =>
                    mapAuthorisationResult([result]) != null
                        ? mapAuthorisationResult([result])[0]
                        : null
                )
            );
    }

    // ── External-only operations ─────────────────────────────────────────────

    /**
     * Fetch all active authorisations for the current operator session from
     * the external provider. Unlike the FlyFreely path, this does not need an
     * explicit mission ID — the external endpoint identifies the operator via
     * the bearer token.
     *
     * Called by `AirspaceAuthorisationManager` Pipeline 5 whenever the token
     * or URL first becomes available, and also after a successful `apply`.
     */
    findAllForMission(
        type: AirspaceAuthorisationDto.AuthorisationType
    ): Observable<AirspaceAuthorisationDto[]> {
        if (!this.checkToken()) {
            return throwError(() => new SessionExpired());
        }
        return this.http
            .get<OperationAuthorisation[]>(
                `${this._url}/authorisations/${type}/operations`,
                { headers: this.buildHeaders() }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 500) {
                        return throwError(
                            () =>
                                ({
                                    code: 500,
                                    message: err.message
                                } as AuthorisationError)
                        );
                    }
                    if (err.status === 404) {
                        return of(null as OperationAuthorisation[]);
                    }
                    if (err.status === 401) {
                        return throwError(() => new SessionExpired());
                    }
                    return throwError(() => parseErrorBody(err));
                }),
                // FIXME: temporary normalisation
                map(result => mapAuthorisationResult(result))
            );
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    /**
     * Returns `true` when a token is available. When the token is missing,
     * restarts bearer-token polling and returns `false` so the caller can
     * throw `SessionExpired`.
     */
    private checkToken(): boolean {
        if (this._token == null) {
            this.airspaceCheckService.restartExternalBearerTokenPolling();
            return false;
        }
        return true;
    }

    private buildHeaders(): HttpHeaders {
        return new HttpHeaders().set(
            'Authorization',
            `Bearer ${this._token.accessToken}`
        );
    }
}
